import React, { useState, useEffect, useContext } from "react";
import { io } from "socket.io-client";
import MainLayout from "../../layouts/MainLayout";
import { AuthContext } from "../../context/AuthContext";
import DashboardServices from "../../services/DashboardServices";
import DashboardSuperAdmin from "./components/DashboardSuperAdmin";
import DashboardAdmin from "./components/DashboardAdmin";
import DashboardOutlet from "./components/DashboardOutlet";
import DashboardMobileUser from "./components/DashboardMobileUser";
import "../../styles/Dashboard.css";

const ROLE_SUPER_ADMIN = 1;
const ROLE_ADMIN = 2;
const ROLE_OUTLET = 3;
const ROLE_MOBILE_USER = 4;
const ROLE_SUB_ADMIN = 5;

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  // Bumped when a game is started, stopped or edited elsewhere, so the
  // dashboard's game cards do not sit on stale figures.
  const [gamesVersion, setGamesVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_NODE_URL, {
      transports: ["websocket", "polling"],
    });
    socket.on("gamesChanged", () => setGamesVersion((v) => v + 1));
    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.role_id) {
        setLoading(false);
        return;
      }
      try {
        setError(null);
        const res = await DashboardServices.getStats();
        // API returns { isSuccess, data: { stats, recentWinners, ... } }; service returns that same object
        const payload = res?.data ?? res;
        if (payload && (payload.stats != null || payload.roleId != null)) {
          setData(payload);
        } else {
          setData({ stats: {}, recentWinners: [], recentActivity: [], settings: null, wallet: null });
        }
      } catch (err) {
        console.error("Error loading dashboard data:", err);
        setError("Failed to load dashboard data.");
        setData({ stats: {}, recentWinners: [], recentActivity: [], settings: null, wallet: null });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.user_id, user?.role_id, gamesVersion]);

  const roleId = Number(user?.role_id) || 4;

  const renderRoleDashboard = () => {
    if (loading) {
      return (
        <div className="dashboard-loading">
          Loading dashboard...
        </div>
      );
    }
    if (error) {
      return <div className="dashboard-error">{error}</div>;
    }
    switch (roleId) {
      case ROLE_SUPER_ADMIN:
        return <DashboardSuperAdmin user={user} data={data} />;
      case ROLE_ADMIN:
      case ROLE_SUB_ADMIN:
        // A sub admin runs a subtree exactly like an admin; the server
        // scopes the numbers to it.
        return <DashboardAdmin user={user} data={data} />;
      case ROLE_OUTLET:
        return <DashboardOutlet user={user} data={data} />;
      case ROLE_MOBILE_USER:
        return <DashboardMobileUser user={user} data={data} />;
      default:
        return <DashboardMobileUser user={user} data={data} />;
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-main">
          <h1>Welcome, {user?.name || "User"}!</h1>
          <div className="dashboard-header-meta">
            <span className="user-id-pill">
              ID: {user?.username || user?.game_id}
            </span>            
          </div>
        </div>
      </div>

      {renderRoleDashboard()}
    </div>
  );
};

export default Dashboard;
